import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router';
import {
  ListItem, List, ListItemText, ListItemSecondaryAction,
  Box,
  Checkbox,
  Stack
} from '@mui/material';
import { useSnackbar } from 'notistack';
import FormFieldFactory from 'components/Form/FormFieldFactory';
import useLoading from 'context/LoadingContext';
import SnackMessage from 'components/SnackMessage/SnackMessage';
import { VITE_PORTAL_API } from 'config';
import useConnectErrorHandler from 'helpers/errorHandlers';
import PAGES from 'common/pages';
import { post, put } from 'helpers/query/fetchers';
import Form from 'components/Form/Form';
import API from 'common/api';
import { Typography } from 'bso-ui';

const initialFormValue = {
  username: '',
  first_name: '',
  last_name: '',
  email: '',
  is_staff: false,
  is_active: false,
  password: ''
};

const initialFormErrorValue = {
  username: [],
  first_name: [],
  last_name: [],
  email: [],
  password: [],
  assign: [],
};

const UserCreate = ({
  title, button, data = null, formType = 'create', refetch = null, handleAssign = false, loggedUserGroups
}) => {
  const [form, setForm] = useState(initialFormValue);
  const [formError, setFormError] = useState(initialFormErrorValue);
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const location = useLocation();
  const { setLoading } = useLoading();
  const { handleError } = useConnectErrorHandler();

  useEffect(() => {
    if (data) {
      setForm(data);
      setFormError(initialFormErrorValue);
    }
    return () => { };
  }, [data]);

  const onChangeValue = (value, name) => {
    setForm({ ...form, [name]: value });
  };

  const [selectedGroups, setSelectedGroups] = useState([]);

  const isChecked = (groupId) => selectedGroups.includes(groupId);

  const handleCheckGroup = (event, groupId) => {
    if (event.target.checked) {
      setSelectedGroups((prev) => [...prev, groupId]);
    } else {
      setSelectedGroups((prev) => prev.filter((x) => x !== groupId));
    }
  };

  const handleSubmit = async (handleClose, setSubmitting) => {
    if (handleAssign && !selectedGroups.length) {
      setFormError({ assign: ['Please assign at least one group.'] });
      return;
    }

    setLoading(true);
    setSubmitting(true);
    const payload = JSON.parse(JSON.stringify(form));

    let url;
    let msg = '';
    if (formType === 'create') {
      try {
        url = `${VITE_PORTAL_API}${API.USER}`;

        const response = await post(url, payload);
        setFormError(initialFormErrorValue);
        setSubmitting(false);
        const userId = response?.data.id;

        // assign group if needed
        if (handleAssign) {
          const assignmentQueue = selectedGroups.map(
            (groupId) => () => post(API.SET_GROUP(userId, groupId))
          );
          const runAssignments = () => Promise.all(assignmentQueue.map((fn) => fn()));
          try {
            await runAssignments();
            // creation AND assignment success
            msg = `USER ID: ${userId} has been created and assigned successfully.`;
            enqueueSnackbar(msg, {
              content: (key) => <SnackMessage id={key} message={msg} variant="success" />
            });
            navigate(`${PAGES.USER_MGMT}/${userId}${location.search}`);
          } catch (assignError) {
            // assignment failed
            msg = `USER ID: ${userId} was created, but group assignment failed.`;
            enqueueSnackbar(msg, {
              content: (key) => (
                <SnackMessage
                  id={key}
                  message={msg}
                  variant="warning"
                  onRetry={async () => {
                    await runAssignments();
                  }}
                />
              ),
            });
          }
        } else {
          // no assignment requested
          msg = `USER ID: ${userId} has been created successfully.`;
          enqueueSnackbar(msg, {
            content: (key) => <SnackMessage id={key} message={msg} variant="success" />
          });
          navigate(`${PAGES.USER_MGMT}/${userId}${location.search}`);
        }
      } catch (error) {
        // create has failed
        setFormError(error.response?.data);
        handleError(error);
        setSubmitting(false);
      }
    } else if (formType === 'update') {
      url = `${VITE_PORTAL_API}${API.USER}${data.id}/`;
      put(url, payload)
        .then((result) => {
          handleClose(true);
          setForm(initialFormValue);
          setFormError(initialFormErrorValue);
          setSubmitting(false);

          setForm(result.data);
          msg = `USER ID: ${result.data.id} has been Updated.`;
          refetch();
          enqueueSnackbar(msg, {
            content: (key) => <SnackMessage id={key} message={msg} variant="success" />
          });
        })
        .catch((error) => {
          setLoading(false);
          setSubmitting(false);
          setFormError(error.response.data);
          handleError(error);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const isError = (error) => error && error.length > 0;

  const loggedUserGroupNames = loggedUserGroups ? loggedUserGroups.map((x) => x.name) : [];

  const renderForm = () => (
    <List disablePadding>
      <ListItem disableGutters>
        <FormFieldFactory
          type="string"
          key="username"
          name="username"
          label="Username"
          // maxLength={fields.name.max_length}
          value={form.username}
          onChange={onChangeValue}
          autoFocus
          required
          error={isError(formError.username)}
          helperText={formError.username ? formError.username[0] : null}
        />
      </ListItem>
      <ListItem disableGutters>
        <FormFieldFactory
          type="string"
          key="first_name"
          name="first_name"
          label="First Name"
          // maxLength={fields.name.max_length}
          value={form.first_name}
          onChange={onChangeValue}
          required
          error={isError(formError.first_name)}
          helperText={formError.first_name ? formError.first_name[0] : null}
        />
      </ListItem>
      <ListItem disableGutters>
        <FormFieldFactory
          type="string"
          key="last_name"
          name="last_name"
          label="Last Name"
          // maxLength={fields.name.max_length}
          value={form.last_name}
          onChange={onChangeValue}
          error={isError(formError.last_name)}
          helperText={formError.last_name ? formError.last_name[0] : null}
        />
      </ListItem>
      <ListItem disableGutters>
        <FormFieldFactory
          type="string"
          key="email"
          name="email"
          label="Email"
          // maxLength={fields.name.max_length}
          value={form.email}
          onChange={onChangeValue}
          error={isError(formError.email)}
          helperText={formError.email ? formError.email[0] : null}
        />
      </ListItem>
      {formType === 'create' ? (
        <ListItem disableGutters>
          <FormFieldFactory
            type="string"
            key="password"
            name="password"
            label="Default Password"
            // maxLength={fields.name.max_length}
            value={form.password}
            onChange={onChangeValue}
            required
            error={isError(formError.password)}
            helperText={formError.password ? formError.password[0] : null}
          />
        </ListItem>
      ) : null}
      <ListItem disableGutters>
        <ListItemText primary="Is Staff" />
        <ListItemSecondaryAction>
          <FormFieldFactory
            type="boolean"
            key="is_staff"
            name="is_staff"
            value="is_staff"
            checked={form.is_staff}
            setToggle={onChangeValue}
            required
          />
        </ListItemSecondaryAction>
      </ListItem>
      <ListItem disableGutters>
        <ListItemText primary="Is Active" />
        <ListItemSecondaryAction>
          <FormFieldFactory
            type="boolean"
            key="is_active"
            name="is_active"
            value="is_active"
            checked={form.is_active}
            setToggle={onChangeValue}
            required
          />
        </ListItemSecondaryAction>
      </ListItem>
      {handleAssign && (
      <Box>
        <Typography>Assign user to group: *</Typography>
        {formError.assign && <Typography color="red">{formError.assign[0]}</Typography>}
        {loggedUserGroups
        && loggedUserGroups.map((group) => {
          if (loggedUserGroupNames.includes(`${group.name}_admins`)) {
            return (
              <Stack alignItems="center" direction="row">
                <Checkbox isChecked={() => isChecked(group.id)} onChange={(e) => handleCheckGroup(e, group.id)} />
                <Typography color="primary" variant="body2" component="span">
                  {group.name}
                </Typography>
              </Stack>
            );
          } return null;
        })}
      </Box>
      )}
    </List>
  );

  return (
    <Form title={title} button={button} renderForm={renderForm} submitForm={handleSubmit} />
  );
};

export default UserCreate;
